import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../hooks/useAuth";
import { useBranding } from "../../hooks/useBranding";
import { useToast } from "../../components/ui/ToastModal";
import { Eye, EyeOff, ArrowRight, ChevronLeft } from "lucide-react";
import {
  getLastSignIn,
  clearLastSignIn,
  maskEmail,
  type LastSignIn,
} from "../../lib/authRecall";
import {
  useIntroGate,
  prefersReducedMotion,
  staggerContainer,
  fadeUpItem,
} from "../../lib/motion";
import { LoginIntro } from "./LoginIntro";
import "./Login.css";
import { logError } from "../../lib/logError";
import { forgetReturnTarget, rememberReturnTarget, safeNextPath } from "../../lib/loginReturn";

function MicrosoftLogo() {
  return (
    <svg
      className="login-sso-icon"
      viewBox="0 0 21 21"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

const FEATURES = [
  "Track progress claims end to end",
  "Manage variations, retention & payments",
  "Smart, shareable claim reports",
];

export function LoginPage() {
  // Capture the last sign-in once so it doesn't change mid-session.
  const [lastSignIn] = useState<LastSignIn | null>(() => getLastSignIn());

  const [mode, setMode] = useState<"recall" | "full">(
    lastSignIn ? "recall" : "full"
  );
  const [email, setEmail] = useState(lastSignIn?.email ?? "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);

  // First-open brand intro (once per session, never under reduced motion).
  const reduced = prefersReducedMotion();
  const playIntro = useIntroGate();
  const [introDone, setIntroDone] = useState(!playIntro);

  const { signIn, signInWithSSO, user, loading: authLoading } = useAuth();
  const { branding } = useBranding();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const logoSrc = branding.logo_url || "/claims-app-logo.svg";

  // /login?next=<path> (a link built elsewhere): validate, remember, and drop
  // it from the URL. A person who is already signed in goes straight to it
  // (AppLayout consumes the stored target). An invalid next is ignored and
  // clears any stale target so it cannot misroute this sign-in.
  const nextParam = searchParams.get("next");
  useEffect(() => {
    if (nextParam === null) return;
    const safe = safeNextPath(nextParam);
    if (safe) rememberReturnTarget(safe, true);
    else forgetReturnTarget();
    if (!authLoading && user) {
      navigate("/", { replace: true });
      return;
    }
    if (authLoading) return;
    const rest = new URLSearchParams(searchParams);
    rest.delete("next");
    setSearchParams(rest, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextParam, authLoading, user]);

  // Show session expired toast when redirected from idle timeout
  useEffect(() => {
    if (searchParams.get("session") === "expired") {
      addToast({
        type: "warning",
        title: "Session Expired",
        message: "You were signed out due to inactivity.",
        duration: 6000,
      });
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSSOLogin = async () => {
    setError(null);
    setSsoLoading(true);

    const { error } = await signInWithSSO();
    if (error) {
      setError(error);
      setSsoLoading(false);
    }
    // If no error, the browser will redirect to Microsoft, keep the spinner.
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error } = await signIn(email, password);
      if (error) {
        setError(error.message);
        setLoading(false);
      } else {
        navigate("/");
      }
    } catch (err) {
      logError("[Login] Unexpected error:", err);
      setError("An unexpected error occurred");
      setLoading(false);
    }
  };

  /** Forget the remembered account and show the full sign-in options. */
  const handleUseDifferentAccount = () => {
    clearLastSignIn();
    setEmail("");
    setPassword("");
    setError(null);
    setMode("full");
  };

  /** Keep the remembered email but reveal all options. */
  const handleSignInAnotherWay = () => {
    setError(null);
    setMode("full");
  };

  const busy = loading || ssoLoading;
  const greetingName = lastSignIn?.name?.split(" ")[0];

  // Initials for the Welcome-back avatar (avoids duplicating the brand logo).
  const initials = (() => {
    const name = lastSignIn?.name?.trim();
    if (name) {
      const parts = name.split(/\s+/);
      return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase();
    }
    const mail = lastSignIn?.email?.trim();
    return mail ? mail[0].toUpperCase() : "";
  })();

  // Motion props applied only when motion is allowed (no-op under reduced motion).
  const itemMotion = reduced ? {} : { variants: fadeUpItem };
  const shellMotion = reduced
    ? {}
    : {
        variants: staggerContainer,
        initial: "hidden" as const,
        animate: introDone ? ("visible" as const) : ("hidden" as const),
      };

  // ── The brand panel (left on desktop, top on mobile) ──
  const brandPanel = (
    <aside className="login-brand-panel">
      <div className="login-brand-panel-inner">
        <motion.div className="login-brand-badge" {...itemMotion}>
          <img src={logoSrc} alt="" className="login-brand-badge-logo" />
        </motion.div>
        <motion.h2 className="login-brand-name" {...itemMotion}>
          {branding.app_name}
        </motion.h2>
        <motion.p className="login-brand-tagline" {...itemMotion}>
          {branding.company_name
            ? `${branding.company_name} · Claims management`
            : "Construction progress & payment claims, simplified."}
        </motion.p>
        <ul className="login-feature-list">
          {FEATURES.map((f) => (
            <motion.li key={f} className="login-feature-item" {...itemMotion}>
              <span className="login-feature-tick" aria-hidden="true">
                <svg viewBox="0 0 16 16" fill="none">
                  <path
                    d="M3.5 8.5l3 3 6-7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              {f}
            </motion.li>
          ))}
        </ul>
      </div>
      <div className="login-brand-glow" aria-hidden="true" />
    </aside>
  );

  // ── Microsoft SSO button (shared) ──
  const ssoButton = (variant: "primary" | "secondary") => (
    <button
      type="button"
      className={
        variant === "primary"
          ? "login-sso-button login-sso-button--primary"
          : "login-sso-button"
      }
      onClick={handleSSOLogin}
      disabled={busy}
    >
      {ssoLoading ? <span className="login-sso-spinner" /> : <MicrosoftLogo />}
      Continue with Microsoft
    </button>
  );

  // ── Email + password form (shared) ──
  const passwordForm = (
    <form onSubmit={handleSubmit} className="login-form">
      {mode === "full" && (
        <div className="login-form-group">
          <label htmlFor="email" className="login-label">
            Email address
          </label>
          <input
            id="email"
            type="email"
            className="login-input"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </div>
      )}

      <div className="login-form-group">
        <label htmlFor="password" className="login-label">
          Password
        </label>
        <div className="login-password-wrapper">
          <input
            id="password"
            type={showPassword ? "text" : "password"}
            className="login-input"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="current-password"
            autoFocus={mode === "recall" && introDone}
            style={{ paddingRight: 44 }}
          />
          <button
            type="button"
            className="login-password-toggle"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
      </div>

      {error && (
        <div className="login-error" role="alert">
          <p>{error}</p>
        </div>
      )}

      <button type="submit" className="login-button" disabled={busy}>
        {loading ? (
          <span className="login-sso-spinner" />
        ) : (
          <>
            Sign in
            <ArrowRight size={18} />
          </>
        )}
      </button>
    </form>
  );

  return (
    <div className="login-container">
      <AnimatePresence>
        {playIntro && !introDone && (
          <LoginIntro
            appName={branding.app_name}
            logoSrc={logoSrc}
            onComplete={() => setIntroDone(true)}
          />
        )}
      </AnimatePresence>

      <motion.div className="login-shell" {...shellMotion}>
        {brandPanel}

        <main className="login-main">
          <div className="login-card">
            {/* Compact logo shown only on mobile (brand panel is hidden there) */}
            <motion.img
              src={logoSrc}
              alt={branding.app_name}
              className="login-logo-mobile"
              {...itemMotion}
            />

            {mode === "recall" && lastSignIn ? (
              <>
                <motion.div className="login-welcome" {...itemMotion}>
                  <div className="login-welcome-avatar" aria-hidden="true">
                    {initials || <img src={logoSrc} alt="" />}
                  </div>
                  <div className="login-welcome-text">
                    <h1 className="login-title">
                      Welcome back{greetingName ? `, ${greetingName}` : ""}
                    </h1>
                    {lastSignIn.email ? (
                      <p className="login-subtitle">{maskEmail(lastSignIn.email)}</p>
                    ) : (
                      <p className="login-subtitle">
                        Continue with{" "}
                        {lastSignIn.method === "microsoft" ? "Microsoft" : "your email"}
                      </p>
                    )}
                  </div>
                </motion.div>

                {lastSignIn.method === "microsoft" ? (
                  <>
                    <motion.div {...itemMotion}>{ssoButton("primary")}</motion.div>
                    {error && (
                      <div className="login-error" role="alert">
                        <p>{error}</p>
                      </div>
                    )}
                    <motion.button
                      type="button"
                      className="login-text-link"
                      onClick={handleSignInAnotherWay}
                      disabled={busy}
                      {...itemMotion}
                    >
                      Sign in another way
                    </motion.button>
                  </>
                ) : (
                  <>
                    <motion.div {...itemMotion}>{passwordForm}</motion.div>
                    <motion.div className="login-divider" {...itemMotion}>
                      <span>or</span>
                    </motion.div>
                    <motion.div {...itemMotion}>{ssoButton("secondary")}</motion.div>
                  </>
                )}

                <motion.button
                  type="button"
                  className="login-text-link login-text-link--muted"
                  onClick={handleUseDifferentAccount}
                  disabled={busy}
                  {...itemMotion}
                >
                  Use a different account
                </motion.button>
              </>
            ) : (
              <>
                {lastSignIn && (
                  <button
                    type="button"
                    className="login-back-link"
                    onClick={() => setMode("recall")}
                    disabled={busy}
                  >
                    <ChevronLeft size={16} />
                    Back
                  </button>
                )}
                <motion.div className="login-header" {...itemMotion}>
                  <h1 className="login-title">Sign in</h1>
                  <p className="login-subtitle">
                    Welcome to {branding.app_name}. Manage your claims.
                  </p>
                </motion.div>

                <motion.div {...itemMotion}>{ssoButton("secondary")}</motion.div>

                <motion.div className="login-divider" {...itemMotion}>
                  <span>or</span>
                </motion.div>

                <motion.div {...itemMotion}>{passwordForm}</motion.div>
              </>
            )}

            <motion.p className="login-footer" {...itemMotion}>
              Invite-only application. Contact an administrator for access.
            </motion.p>
          </div>
        </main>
      </motion.div>
    </div>
  );
}
